import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FilterBar, { defaultFilters, type Filters } from '@/pages/category/components/FilterBar';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { movies } from '@/mocks/movies';
import { filterMovies, sortMovies } from '@/lib/movies';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 12;

const parseFromSearch = (search: string): Partial<Filters> => {
  const params = new URLSearchParams(search);
  const patch: Partial<Filters> = {};
  const q = params.get('q');
  const type = params.get('type');
  const region = params.get('region');
  const year = params.get('year');
  const genre = params.get('genre');
  if (q) patch.q = q;
  if (type) patch.type = type;
  if (region) patch.region = region;
  if (year) patch.year = year;
  if (genre) patch.genre = genre;
  return patch;
};

export default function Category() {
  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>({ ...defaultFilters, ...parseFromSearch(location.search) });

  useEffect(() => {
    const patch = parseFromSearch(location.search);
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
  }, [location.search]);

  const result = useMemo(() => {
    const filtered = filterMovies(movies, {
      q: filters.q,
      type: filters.type,
      genre: filters.genre,
      region: filters.region,
      year: filters.year,
    });
    return sortMovies(filtered, filters.sort);
  }, [filters]);

  const totalPages = Math.ceil(result.length / PAGE_SIZE);
  const visibleCount = filters.loadMode === 'more' ? filters.page * PAGE_SIZE : filters.page * PAGE_SIZE;
  const start = filters.loadMode === 'more' ? 0 : (filters.page - 1) * PAGE_SIZE;
  const list = result.slice(start, visibleCount);

  const heading = filters.q ? `“${filters.q}” 的搜索结果` : filters.type !== '全部' ? `${filters.type}分类` : '影视分类';
  const description = filters.q
    ? `为你找到与关键词“${filters.q}”相关的影视作品，可继续按类型、题材、地区与年份筛选。`
    : '浏览站内全部影视作品，通过类型、题材、地区与年份筛选，快速定位你感兴趣的内容。';

  useSeo({
    title: `${heading} - 麻豆传媒视频`,
    description: `${description} 影视资料为演示数据，仅供界面展示。`,
    keywords: `${filters.q || filters.type} 影视,分类筛选,影视列表,麻豆传媒视频`,
  });

  const update = (patch: Partial<Filters>) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      if (patch.q !== undefined || patch.type !== undefined) {
        const params = new URLSearchParams();
        if (next.q) params.set('q', next.q);
        if (next.type && next.type !== '全部') params.set('type', next.type);
        const qs = params.toString();
        navigate(`/category${qs ? `?${qs}` : ''}`, { replace: true });
      }
      return next;
    });
  };

  const reset = () => {
    setFilters({ ...defaultFilters });
    navigate('/category', { replace: true });
  };

  const handlePage = (next: number) => {
    setFilters((prev) => ({ ...prev, page: next }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/category" className="hover:text-primary-700">
              影视分类
            </Link>
            {filters.type !== '全部' && (
              <>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-800">{filters.type}</span>
              </>
            )}
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{heading}</h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">{description}</p>
          {filters.q && (
            <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-800">
              <i className="ri-search-line"></i>
              当前关键词：{filters.q}（搜索结果为前端演示）
            </span>
          )}
        </div>
      </div>

      <div className="mx-auto w-full px-4 md:px-8 py-8 md:py-10">
        <FilterBar filters={filters} onChange={update} onReset={reset} />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            共找到 <strong className="text-foreground-950">{result.length}</strong> 部影视内容
          </p>
          <div className="flex items-center gap-1 rounded-full bg-background-200/70 p-1">
            <button
              type="button"
              onClick={() => setFilters((prev) => ({ ...prev, loadMode: 'page', page: 1 }))}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filters.loadMode === 'page' ? 'bg-background-50 text-foreground-950' : 'text-foreground-600'
              }`}
            >
              分页浏览
            </button>
            <button
              type="button"
              onClick={() => setFilters((prev) => ({ ...prev, loadMode: 'more', page: 1 }))}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filters.loadMode === 'more' ? 'bg-background-50 text-foreground-950' : 'text-foreground-600'
              }`}
            >
              加载更多
            </button>
          </div>
        </div>

        {list.length === 0 ? (
          <div className="mt-6">
            <EmptyState
              icon="ri-search-eye-line"
              title="没有找到匹配的影视内容"
              description="换个关键词，或调整类型、地区、年份等筛选条件试试看。"
              actionLabel="重置筛选条件"
              onAction={reset}
            />
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 md:gap-5 lg:grid-cols-4">
              {list.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>

            {filters.loadMode === 'page' ? (
              <Pagination current={filters.page} total={totalPages} onChange={handlePage} />
            ) : (
              filters.page < totalPages && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                    className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                    加载更多（剩余 {result.length - list.length} 部）
                  </button>
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}